import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil } from 'lucide-react';
import { useActivateCoupon, useCouponDetail, useDeactivateCoupon } from './hooks';
import { CouponStatusBadge } from './components/CouponStatusBadge';
import { CouponForm } from './components/CouponForm';
import { CouponUsageTable } from './components/CouponUsageTable';
import { ConfirmModal } from '@/src/modules/components/ui/ConfirmModal';
import { TableSkeleton } from '@/src/modules/components/ui/LoadingSkeleton';
import { EmptyState, ErrorState } from '@/src/modules/components/ui/EmptyState';
import { ApiError } from './services/api-client';
import { browserTimeZone, describeDiscount, formatDateTime } from './lib/discount';
import type { CouponDetail } from './types';

const primaryBtn =
  'px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50';

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="glass-panel rounded-xl p-4 space-y-1">
      <p className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">{title}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

export default function CouponDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: coupon, isLoading, isError, error, refetch } = useCouponDetail(id);

  if (isLoading) return <TableSkeleton rows={4} cols={3} />;
  if (isError && error instanceof ApiError && error.status === 404) {
    return (
      <EmptyState
        title="Coupon not found"
        action={
          <Link to="/coupons" className="text-xs underline">
            Back to coupons
          </Link>
        }
      />
    );
  }
  if (isError || !coupon) {
    return <ErrorState message={(error as Error)?.message ?? 'Coupon not found'} onRetry={() => void refetch()} />;
  }
  return <CouponDetailView coupon={coupon} onReload={() => void refetch()} />;
}

function CouponDetailView({ coupon, onReload }: { coupon: CouponDetail; onReload: () => void }) {
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<'activate' | 'deactivate' | null>(null);
  const [reason, setReason] = useState('');
  const activate = useActivateCoupon(coupon.id);
  const deactivate = useDeactivateCoupon(coupon.id);
  const lifecycle = confirm === 'activate' ? activate : deactivate;

  const canActivate = coupon.status === 'DRAFT' || coupon.status === 'INACTIVE';
  const canDeactivate = coupon.status === 'ACTIVE';
  const terminal = coupon.status === 'EXPIRED' || coupon.status === 'EXHAUSTED';
  const limit = coupon.usageLimitGlobal;
  const scopeText =
    coupon.collectionCodes.length === 0 ? 'All products' : `${coupon.collectionCodes.length} selected product(s)`;

  const closeConfirm = () => {
    setConfirm(null);
    setReason('');
    activate.reset();
    deactivate.reset();
  };

  const runLifecycle = async () => {
    try {
      if (confirm === 'activate') await activate.mutateAsync();
      else await deactivate.mutateAsync(reason.trim() || undefined);
      closeConfirm();
    } catch {
      // Status moved underneath us (e.g. expiry job) — show the error, refresh the truth.
      onReload();
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/coupons" className="text-xs text-on-surface-variant hover:text-on-surface flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to coupons
      </Link>

      <div className="glass-panel rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-mono text-2xl font-bold select-all">{coupon.code}</h2>
            <CouponStatusBadge coupon={coupon} />
          </div>
          <p className="text-sm text-on-surface-variant mt-1 break-words">{coupon.label}</p>
          {coupon.status === 'DRAFT' && (
            <p className="text-xs text-on-surface-variant mt-2">Draft — customers can't redeem it until you activate it.</p>
          )}
          {terminal && (
            <p className="text-xs text-on-surface-variant mt-2">
              This coupon can't be reactivated. Create a new code instead.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 px-4 py-2 rounded-lg border border-border-subtle text-xs font-bold uppercase tracking-widest hover:bg-white/5"
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
          {canActivate && (
            <button onClick={() => setConfirm('activate')} className={`${primaryBtn} bg-emerald-600 hover:bg-emerald-500`}>
              Activate
            </button>
          )}
          {canDeactivate && (
            <button onClick={() => setConfirm('deactivate')} className={`${primaryBtn} bg-red-600 hover:bg-red-500`}>
              Deactivate
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card title="Discount">{describeDiscount(coupon)}</Card>
        <Card title="Validity">
          <div>{formatDateTime(coupon.startAt)}</div>
          <div>→ {formatDateTime(coupon.endAt)}</div>
          <div className="text-[10px] text-on-surface-variant">{browserTimeZone()}</div>
        </Card>
        <Card title="Usage">
          <div>
            {coupon.usedCount} / {limit ?? '∞'}
          </div>
          {limit !== null && (
            <div className="h-1.5 rounded-full bg-surface-container-highest mt-1 overflow-hidden">
              <div
                className="h-full bg-electric-violet"
                style={{ width: `${Math.min(100, (coupon.usedCount / limit) * 100)}%` }}
              />
            </div>
          )}
          <div className="text-[10px] text-on-surface-variant mt-1">
            Per customer: {coupon.usageLimitPerUser ?? 'unlimited'}
          </div>
        </Card>
        <Card title="Applies to">
          <div>{scopeText}</div>
          {coupon.collectionCodes.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {coupon.collectionCodes.map((c) => (
                <span key={c} className="px-1.5 py-0.5 rounded bg-surface-container-high text-[10px] font-mono">
                  {c}
                </span>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="space-y-2">
        <h3 className="font-display text-lg font-bold">Redemptions</h3>
        <CouponUsageTable couponId={coupon.id} />
      </div>

      <p className="text-[10px] text-on-surface-variant">
        Created {formatDateTime(coupon.createdAt)} by <span className="font-mono">{coupon.createdByAdminId}</span> ·
        Updated {formatDateTime(coupon.updatedAt)} · Version {coupon.version}
      </p>

      <CouponForm open={editing} mode="edit" coupon={coupon} onClose={() => setEditing(false)} onReload={onReload} />

      <ConfirmModal
        open={confirm !== null}
        title={confirm === 'activate' ? `Activate ${coupon.code}?` : `Deactivate ${coupon.code}?`}
        description={
          confirm === 'activate'
            ? `Customers can redeem it from ${formatDateTime(coupon.startAt)} to ${formatDateTime(coupon.endAt)} on ${scopeText.toLowerCase()} (${describeDiscount(coupon)}).`
            : 'Customers will no longer be able to apply this code. You can reactivate it later.'
        }
        confirmLabel={confirm === 'activate' ? 'Activate' : 'Deactivate'}
        destructive={confirm === 'deactivate'}
        loading={lifecycle.isPending}
        onClose={closeConfirm}
        onConfirm={() => void runLifecycle()}
      >
        {confirm === 'deactivate' && (
          <div>
            <label htmlFor="deactivate-reason" className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
              Reason (optional, kept in the audit log)
            </label>
            <textarea
              id="deactivate-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none"
            />
          </div>
        )}
        {lifecycle.error && <p className="text-xs text-red-300">{(lifecycle.error as Error).message}</p>}
      </ConfirmModal>
    </div>
  );
}
